import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' });
const authOnly = () => ({ 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` });
const PART_SIZE = 15 * 1024 * 1024; // 15MB browser slices

const fmtSize = (b) => {
  if (!b) return '0 MB';
  const mb = b / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
};
const fmtDur = (s) => {
  if (!s) return '0s';
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
};

const FootagePreprocessor = () => {
  const [jobs, setJobs] = useState([]);
  const [options, setOptions] = useState({ resolutions: [], segment_lengths: [] });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/preproc/jobs`, { headers: authOnly() });
      if (res.ok) setJobs(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetch(`${API}/api/admin/preproc/options`, { headers: authOnly() }).then(r => r.ok && r.json().then(setOptions));
  }, [fetchJobs]);

  if (selected) {
    return <JobDetail jobId={selected} onBack={() => { setSelected(null); fetchJobs(); }} />;
  }

  return (
    <div className="max-w-6xl" data-testid="footage-preprocessor">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🎞️ Footage Preprocessor</h1>
          <p className="text-gray-500 text-sm mt-1">Turn heavy GoPro 4K files into lightweight ≤30s proxy chunks the AI can digest. Your original stays on your computer.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-preproc-btn"
          className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">+ New Batch</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" /></div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-200" data-testid="preproc-empty">
          <div className="text-5xl mb-3">🎞️</div>
          <h3 className="text-lg font-semibold text-gray-800">No batches yet</h3>
          <p className="text-gray-500 text-sm mt-1 mb-4">Create a batch, add your big 4K clips (upload or Google Drive), and get AI-ready chunks.</p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">+ New Batch</button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="preproc-list">
          {jobs.map(j => (
            <motion.button key={j.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              onClick={() => setSelected(j.id)} data-testid={`job-card-${j.id}`}
              className="text-left bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{j.resolution}p · {j.segment_seconds}s chunks</span>
                <StatusPill status={j.status} />
              </div>
              <h3 className="font-semibold text-gray-900 mt-1 truncate">{j.name}</h3>
              <p className="text-xs text-gray-400 mt-2 truncate">{j.source_filename || (j.source_type === 'drive' ? 'Google Drive' : 'Upload')}</p>
              <p className="text-xs text-gray-500 mt-1">{j.chunk_count} chunk{j.chunk_count === 1 ? '' : 's'} · {fmtSize(j.total_output_size)}</p>
              {(j.status === 'processing' || j.status === 'uploading') && (
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 transition-all" style={{ width: `${j.progress || 0}%` }} />
                </div>
              )}
            </motion.button>
          ))}
        </div>
      )}

      {showCreate && <CreateBatchModal options={options} onClose={() => setShowCreate(false)}
        onCreated={(id) => { setShowCreate(false); fetchJobs(); setSelected(id); }} />}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    created: 'bg-gray-100 text-gray-600', uploading: 'bg-blue-100 text-blue-700',
    processing: 'bg-purple-100 text-purple-700', ready: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700',
  };
  const label = { created: 'New', uploading: 'Uploading…', processing: 'Processing…', ready: 'Ready', failed: 'Failed' }[status] || status;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100'}`} data-testid="job-status">{label}</span>;
};

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const CreateBatchModal = ({ options, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', resolution: '720', segment_seconds: 30, source_type: 'upload' });
  const [driveUrl, setDriveUrl] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const start = async () => {
    if (!form.name.trim()) { setMsg('Please name this batch.'); return; }
    if (form.source_type === 'upload' && !file) { setMsg('Choose a video file to upload.'); return; }
    if (form.source_type === 'drive' && !driveUrl.trim()) { setMsg('Paste a Google Drive link.'); return; }
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`${API}/api/admin/preproc/jobs`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ...form, drive_url: form.source_type === 'drive' ? driveUrl.trim() : '' }),
      });
      if (!res.ok) throw new Error('Could not create the batch.');
      const job = await res.json();

      if (form.source_type === 'upload') {
        const totalParts = Math.ceil(file.size / PART_SIZE);
        for (let part = 0; part < totalParts; part++) {
          const blob = file.slice(part * PART_SIZE, (part + 1) * PART_SIZE);
          const fd = new FormData();
          fd.append('file', blob, `part_${part}`);
          fd.append('part', part);
          const up = await fetch(`${API}/api/admin/preproc/jobs/${job.id}/upload-part`, { method: 'POST', headers: authOnly(), body: fd });
          if (!up.ok) throw new Error(`Upload failed on part ${part + 1}/${totalParts}.`);
          setProgress(Math.round(((part + 1) / totalParts) * 100));
          setMsg(`Uploading ${part + 1}/${totalParts} parts…`);
        }
        const fd = new FormData();
        fd.append('filename', file.name);
        fd.append('total_parts', totalParts);
        await fetch(`${API}/api/admin/preproc/jobs/${job.id}/finalize-upload`, { method: 'POST', headers: authOnly(), body: fd });
      } else {
        await fetch(`${API}/api/admin/preproc/jobs/${job.id}/from-drive`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ drive_url: driveUrl.trim() }),
        });
      }
      onCreated(job.id);
    } catch (e) {
      setMsg(e.message || 'Something went wrong.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-batch-modal">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-900 mb-4">New Preprocessing Batch</h2>
        <div className="space-y-4">
          <Field label="Batch name">
            <input value={form.name} onChange={e => set('name', e.target.value)} data-testid="batch-name" placeholder="e.g. Shelly Beach Dive — raw"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Proxy quality">
              <select value={form.resolution} onChange={e => set('resolution', e.target.value)} data-testid="batch-resolution" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                {options.resolutions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Chunk length">
              <select value={form.segment_seconds} onChange={e => set('segment_seconds', Number(e.target.value))} data-testid="batch-seglen" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                {options.segment_lengths.map(s => <option key={s} value={s}>{s} seconds</option>)}
              </select>
            </Field>
          </div>
          <Field label="Source">
            <div className="flex gap-2">
              {[['upload', '⬆️ Upload file'], ['drive', '📁 Google Drive']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set('source_type', v)} data-testid={`source-${v}`}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${form.source_type === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>{l}</button>
              ))}
            </div>
          </Field>

          {form.source_type === 'upload' ? (
            <Field label="Video file" hint="Large 4K files are sliced and uploaded in parts. Very large (multi-GB) files may take a while.">
              <input ref={fileRef} type="file" accept="video/*" data-testid="batch-file"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-sm file:font-medium hover:file:bg-indigo-100" />
              {file && <p className="text-xs text-gray-500 mt-1">{file.name} · {fmtSize(file.size)}</p>}
            </Field>
          ) : (
            <Field label="Google Drive link" hint="Share as 'Anyone with the link'. A file link or a folder link both work.">
              <input value={driveUrl} onChange={e => setDriveUrl(e.target.value)} data-testid="batch-drive-url" placeholder="https://drive.google.com/…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            </Field>
          )}
        </div>

        {busy && (
          <div className="mt-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} /></div>
            <p className="text-xs text-indigo-600 mt-1">{msg}</p>
          </div>
        )}
        {!busy && msg && <p className="text-xs text-red-500 mt-3" data-testid="batch-error">{msg}</p>}

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50">Cancel</button>
          <button onClick={start} disabled={busy} data-testid="start-batch-submit" className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">{busy ? 'Working…' : 'Start'}</button>
        </div>
      </motion.div>
    </div>
  );
};

const JobDetail = ({ jobId, onBack }) => {
  const [job, setJob] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/admin/preproc/jobs/${jobId}`, { headers: authOnly() });
    if (res.ok) setJob(await res.json());
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const active = job?.status === 'processing' || job?.status === 'uploading';
    if (active && !pollRef.current) pollRef.current = setInterval(load, 3000);
    else if (!active && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [job?.status, load]);

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/admin/preproc/jobs/${jobId}/download-all`, { headers: authOnly() });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${job.name}-chunks.zip`;
      document.body.appendChild(a); a.click(); a.remove();
    } finally { setDownloading(false); }
  };

  const sendToStudio = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/preproc/jobs/${jobId}/to-studio`, { method: 'POST', headers: authHeaders() });
      if (res.ok) { const d = await res.json(); alert(`Created a Video Studio project with ${d.clip_count} clips. Open Auto Video Studio to render — the final Reel is cut from your original 4K (not the 720p proxy) for full quality.`); }
    } finally { setSending(false); }
  };

  if (!job) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" /></div>;
  const chunks = job.chunks || [];

  return (
    <div className="max-w-6xl" data-testid="job-detail">
      <button onClick={onBack} data-testid="back-btn" className="text-sm text-indigo-600 hover:underline mb-3">← All batches</button>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2"><h1 className="text-2xl font-bold text-gray-900">{job.name}</h1><StatusPill status={job.status} /></div>
          <p className="text-gray-500 text-sm mt-1">{job.resolution}p · {job.segment_seconds}s chunks · source {fmtDur(job.source_duration)} · {job.chunk_count} chunk{job.chunk_count === 1 ? '' : 's'} · {fmtSize(job.total_output_size)}{job.has_original ? ' · 4K original kept for HQ re-cut' : ''}</p>
        </div>
        {job.status === 'ready' && chunks.length > 0 && (
          <div className="flex gap-3">
            <button onClick={downloadAll} disabled={downloading} data-testid="download-all-btn"
              className="px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-700 rounded-lg text-sm font-medium">{downloading ? 'Zipping…' : '⬇️ Download all (.zip)'}</button>
            <button onClick={sendToStudio} disabled={sending} data-testid="send-studio-btn"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">{sending ? 'Sending…' : '🎬 Send to Video Studio'}</button>
          </div>
        )}
      </div>

      {(job.status === 'processing' || job.status === 'uploading') && (
        <div className="mb-6 p-4 bg-indigo-50 rounded-xl" data-testid="job-progress">
          <div className="flex items-center gap-2 mb-2 text-indigo-700 text-sm font-medium">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
            {job.status === 'uploading' ? 'Uploading source…' : job.source_type === 'drive' ? 'Downloading from Drive & processing…' : 'Processing footage…'} ({job.progress || 0}%)
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden"><div className="h-full bg-indigo-500 transition-all" style={{ width: `${job.progress || 0}%` }} /></div>
        </div>
      )}
      {job.status === 'failed' && job.error && <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="job-error">Failed: {job.error}</div>}

      {chunks.length === 0 ? (
        job.status === 'ready' ? <div className="text-center py-14 bg-white rounded-xl border border-dashed border-gray-200 text-sm text-gray-500">No chunks were produced.</div> : null
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="chunks-grid">
          {chunks.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-2" data-testid={`chunk-${c.index}`}>
              <video src={`${API}${c.url}`} controls preload="metadata" className="w-full rounded-lg bg-black aspect-video object-cover" data-testid={`chunk-video-${c.index}`} />
              <div className="mt-1.5">
                <div className="text-xs font-medium text-gray-700 truncate">#{c.index + 1} · {fmtDur(c.duration)}</div>
                <div className="text-[10px] text-gray-400">{fmtSize(c.size)}</div>
                <a href={`${API}${c.url}`} download={c.filename} target="_blank" rel="noreferrer" data-testid={`chunk-download-${c.index}`}
                  className="inline-block mt-1 text-xs text-indigo-600 hover:underline">Download</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FootagePreprocessor;
